import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const api = axios.create({
  baseURL: API_URL,
});

// Add auth token to all requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Categories API
export const categoriesApi = {
  list: () => api.get('/categories'),
  get: (slug) => api.get(`/categories/${slug}`),
};

// Skills API
export const skillsApi = {
  list: (params) => api.get('/skills', { params }),
  get: (id) => api.get(`/skills/${id}`),
  create: (data) => api.post('/skills', data),
  update: (id, data) => api.patch(`/skills/${id}`, data),
  delete: (id) => api.delete(`/skills/${id}`),
  publish: (id, visibility) => api.post(`/skills/${id}/publish`, { visibility }),
  updateVisibility: (id, visibility) => api.patch(`/skills/${id}/visibility`, { visibility }),
  validate: (skillMd) => api.post('/validate-skill', { skill_md: skillMd }),
  trackInstall: (id) => api.post(`/skills/${id}/install`),
  downloadPackage: (id) => api.get(`/skills/${id}/download`, { responseType: 'blob' }),
  featured: () => api.get('/skills/featured'),
  trending: () => api.get('/skills/trending'),
  generateSpec: (data) => api.post('/builder/generate-spec', data),
  testSkill: (data) => api.post('/builder/test-skill', data),
  enhanceDescription: (data) => api.post('/builder/enhance-description', data),
  detectCategory: (data) => api.post('/builder/detect-category', data),
};

// Users API
export const usersApi = {
  get: (id) => api.get(`/users/${id}`),
  update: (data) => api.patch('/users/me', data),
  updateSettings: (data) => api.patch('/users/settings', data),
  changePassword: (data) => api.patch('/users/password', data),
  setup2FA: () => api.post('/users/2fa/setup'),
  verify2FA: (data) => api.post('/users/2fa/verify', data),
  disable2FA: (data) => api.post('/users/2fa/disable', data),
  get2FAStatus: () => api.get('/users/2fa/status'),
  requestDataExport: () => api.post('/users/data-export'),
  deleteAccount: () => api.delete('/users/account'),
};

// Social API
export const socialApi = {
  follow: (userId) => api.post('/social/follow', { following_id: userId }),
  unfollow: (userId) => api.delete(`/social/unfollow/${userId}`),
  isFollowing: (userId) => api.get(`/social/is-following/${userId}`),
  star: (skillId) => api.post(`/social/star/${skillId}`),
  unstar: (skillId) => api.delete(`/social/unstar/${skillId}`),
  isStarred: (skillId) => api.get(`/social/is-starred/${skillId}`),
  starredSkills: (params) => api.get('/social/starred-skills', { params }),
  fork: (skillId) => api.post(`/social/fork/${skillId}`),
};

// Playground API
export const playgroundApi = {
  test: (skillId, inputText) => api.post('/playground/test', { skill_id: skillId, input_text: inputText }),
};

// Notifications API
export const notificationsApi = {
  list: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
};

// Activities API
export const activitiesApi = {
  list: () => api.get('/activities'),
};

// Analytics API
export const analyticsApi = {
  getSkillAnalytics: (skillId) => api.get(`/analytics/skills/${skillId}`),
};

// Skill Assistant API
export const skillAssistantApi = {
  start: () => api.post('/skill-assistant/start'),
  chat: (conversationId, message, messageHistory = []) => api.post('/skill-assistant/chat', { 
    conversation_id: conversationId, 
    message,
    message_history: messageHistory
  }),
  getConversations: () => api.get('/skill-assistant/conversations'),
  getConversation: (conversationId) => api.get(`/skill-assistant/conversation/${conversationId}`),
  toggleStar: (conversationId) => api.patch(`/skill-assistant/conversation/${conversationId}/star`),
  renameConversation: (conversationId, title) => api.patch(`/skill-assistant/conversation/${conversationId}/rename?title=${encodeURIComponent(title)}`),
  deleteConversation: (conversationId) => api.delete(`/skill-assistant/conversation/${conversationId}`),
};